import {
  DEFAULT_DEBOUNCE_DELAY,
  IPaginationMetadata,
  STATUSPAGE,
} from '@repo/ui';
import debounce from 'debounce';
import { GetAllArchivedGroupsUseCaseInterface } from 'domain/usecases';
import { GROUPS_ORDER_COLUMNS } from 'domain/value-objects/groups-order-columns';
import { ORDER_TYPE } from 'domain/value-objects/order-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearArchivedGroupsListData,
  loadArchivedGroupsListData,
  loadArchivedGroupsListFilters,
} from 'presentation/config/store/groups/archived-groups-list-slice';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { IArchivedGroupsListFilters } from '../models/archived-groups-list-filters';

export const useArchivedGroupsList = () => {
  // Hooks
  const { groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();
  const navigate = useNavigate();

  // States
  const { archivedGroupsListData, archivedGroupsListFilters } = useAppSelector(
    (state) => state.archivedGroupsList,
  );
  const [paginationMetadata, setPaginationMetadata] = useState<
    IPaginationMetadata<GROUPS_ORDER_COLUMNS>
  >({
    selectedPage: 0,
    rowsPerPage: 10,
    orderColumn: GROUPS_ORDER_COLUMNS.NAME,
    sortDirection: ORDER_TYPE.ASC,
    searchString: '',
  });
  const [listLoadingStatus, setListLoadingStatus] = useState(
    STATUSPAGE.LOADING,
  );

  // Methods
  const changePaginationMetadata = useCallback(
    <T extends keyof IPaginationMetadata<GROUPS_ORDER_COLUMNS>>(
      key: T,
      value: IPaginationMetadata<GROUPS_ORDER_COLUMNS>[T],
    ) => {
      setPaginationMetadata((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const handleSortRemote = useCallback(
    (
      selectedColumn: TableColumn<
        GetAllArchivedGroupsUseCaseInterface.Result['data'][number]
      >,
      sortDirection: SortOrder,
    ) => {
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as GROUPS_ORDER_COLUMNS,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const requestGetAllArchivedGroups = useCallback(
    async (params: GetAllArchivedGroupsUseCaseInterface.Params) => {
      return groupService
        .getAllArchivedGroups(params)
        .then((res) => {
          dispatch(loadArchivedGroupsListData(res));
        })
        .catch((err) => {
          dispatch(clearArchivedGroupsListData());
          notifyError(err.message);
        })
        .finally(() => {
          setListLoadingStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const debouncedRequestGetAllArchivedGroups = useMemo(
    () =>
      debounce(
        (valuesToSubmit: GetAllArchivedGroupsUseCaseInterface.Params) => {
          setListLoadingStatus(STATUSPAGE.LOADING);
          requestGetAllArchivedGroups(valuesToSubmit);
        },
        DEFAULT_DEBOUNCE_DELAY,
      ),
    [],
  );

  const saveArchivedGroupsListFilters = useCallback(
    (params: IArchivedGroupsListFilters) => {
      dispatch(loadArchivedGroupsListFilters(params));
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: archivedGroupsListFilters?.entity || null,
      course: archivedGroupsListFilters?.course || null,
      semester: archivedGroupsListFilters?.semester || null,
      unit: archivedGroupsListFilters?.unit || null,
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      curricularUnit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
    }),

    // There is no onSubmit function on this form (no submit button).
    // The API request is sent everytime any filter changes, so it is controlled via useEffect and DataTable's remote pagination.
    onSubmit: () => {
      return;
    },
  });

  return {
    validation,
    paginationMetadata,
    listLoadingStatus,
    archivedGroupsListData,
    debouncedRequestGetAllArchivedGroups,
    getLegends,
    navigate,
    setListLoadingStatus,
    handleSortRemote,
    handleSearchRemote,
    handlePageChange,
    handleRowsPerPageChange,
    saveArchivedGroupsListFilters,
  };
};
